/**
 * @file bookOver.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Booking an order's invoice over to another month (Cam, 3 Nov
 *              2026 — "Booking over: need to be able to easily change the
 *              invoice date and due date to a different month than the order,
 *              adding a note to the sales description (manual entry) for when
 *              users need to book over an order", and "Booking over can often
 *              need splitting over multiple months so this workflow needs a
 *              modal asking user for info then the system actions it for
 *              them").
 *
 *   The one rule for Pricing & Costing's Invoice window
 *   (InvoiceBookOverDialog) and the invoicing it starts
 *   (services/pricingCostingService.planInvoicing →
 *   orderInvoiceService.invoiceOrderBookedOver). Written once here;
 *   utils/bookOver.js on the server is GENERATED from it by
 *   scripts/sync-shared-utils.js.
 *
 *   - A booking is one or more PARTS. Each part is one invoice: its invoice
 *     date, its due date and the note added to every line's description.
 *     One part: the whole order on one invoice, dated as chosen (and every
 *     order of a bulk run alike). Two to twelve parts: one order split over
 *     several invoices — each part says how much of each order line it
 *     carries; every line's parts add up exactly to what the line invoices
 *     (supplied, else ordered, plus any tonnage added to it).
 *   - Dates: the invoice date never on or before Xero's lock (the first
 *     allowed date or later); the due date never before its invoice date.
 *     Defaults: the 1st of each month after the delivery month in turn, due
 *     the 18th of the month after the invoice date (the terms every order
 *     invoice takes — orderInvoiceService.defaultDueDate).
 *   - The note: typed by hand, required, one line, at most 200 characters.
 *     "{delivery date}" in it becomes each order's own delivery date (the
 *     redelivery's once redelivered) — how one note serves a bulk run.
 *   - 1.1.0 (2026-11-03): an invoice made by hand (no order) is booked over
 *     too — its note starts "Invoiced <date>" (`what`) rather than "Delivered".
 *   - Quantities to 4 decimal places. The equal split takes whole packs for a
 *     line counted in packs (when there are at least as many as parts), else
 *     2 places; the last part takes the remainder, so the parts add up
 *     exactly.
 */

import { formatQuantity } from './numberFormatters';

/** At most this many characters in the note. */
export const BOOK_OVER_NOTE_MAX = 200;
/** At most this many invoices in one booking. */
export const BOOK_OVER_MAX_PARTS = 12;
/** In a note, the order's own delivery date (filled in per order). */
export const DELIVERY_DATE_TOKEN = '{delivery date}';
/** The migration that lets one order carry several invoices. */
export const BOOK_OVER_MIGRATION = 'invoice_booked_over.sql';
export const SPLIT_NOT_READY = `Not ready — migration ${BOOK_OVER_MIGRATION} has not run`;
/** Xero's limit on a line's description (and the check the invoice edit offer makes). */
export const DESCRIPTION_MAX = 4000;
/** Joins a line's own words and the note. */
export const NOTE_JOIN = ' — ';

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
export const round4 = (n) => Math.round(num(n) * 10000) / 10000;
export const round2 = (n) => Math.round(num(n) * 100) / 100;

/** 'YYYY-MM-DD' of a value (a Date, an ISO string), or ''. */
export function dayOf(v) {
  if (!v) return '';
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  return String(v).slice(0, 10);
}

/** Is this a real calendar day, 'YYYY-MM-DD'? */
export function isDay(v) {
  const m = DAY_RE.exec(String(v || ''));
  if (!m) return false;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}

/** The 1st of the month `months` after `day`'s month ('2026-10-15', 1 → '2026-11-01'). */
export function firstOfMonthAfter(day, months = 1) {
  const m = DAY_RE.exec(dayOf(day));
  if (!m) return null;
  const idx = Number(m[1]) * 12 + (Number(m[2]) - 1) + months;
  return `${Math.floor(idx / 12)}-${pad((idx % 12) + 1)}-01`;
}

/** Terms: the 18th of the month after the invoice date. */
export function dueDateFor(invoiceDay) {
  const first = firstOfMonthAfter(invoiceDay, 1);
  return first ? `${first.slice(0, 8)}18` : null;
}

/** 'DD/MM/YYYY'. */
export function ukDay(day) {
  const m = DAY_RE.exec(dayOf(day));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** 'November 2026'. */
export function monthWords(day) {
  const m = DAY_RE.exec(dayOf(day));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
}

/** The later of two days ('' / null lose). */
const laterDay = (a, b) => (!a ? (b || null) : (!b ? a : (a > b ? a : b)));

/**
 * A booking's default invoice date: the 1st of the month after the latest
 * delivery among the orders, never before Xero's first allowed date.
 */
export function defaultInvoiceDate(deliveryDays, firstAllowedDate = null) {
  const latest = (deliveryDays || []).map(dayOf).filter(isDay).sort().pop();
  const first = latest ? firstOfMonthAfter(latest, 1) : null;
  return laterDay(first, firstAllowedDate && isDay(firstAllowedDate) ? firstAllowedDate : null);
}

/**
 * The note a part starts with (typed over freely).
 *   one order, one invoice:  "Delivered 15/10/2026 — booked over to November 2026"
 *   one order, split:        "Delivered 15/10/2026 — booked over, part 1 of 3"
 *   several orders (many):   "Delivered {delivery date} — booked over to November 2026"
 */
export function defaultNote({ deliveryDate = null, invoiceDate = null, part = 1, of = 1, many = false, what = 'Delivered' } = {}) {
  const delivered = many || !isDay(dayOf(deliveryDate)) ? DELIVERY_DATE_TOKEN : ukDay(deliveryDate);
  if (of > 1) return `${what} ${delivered} — booked over, part ${part} of ${of}`;
  return `${what} ${delivered} — booked over to ${monthWords(invoiceDate) || 'another month'}`;
}

/** The note for one order: "{delivery date}" becomes its delivery date. */
export function fillNote(note, deliveryDate) {
  const text = String(note == null ? '' : note);
  const when = ukDay(deliveryDate);
  return when ? text.replace(/\{\s*delivery\s+date\s*\}/gi, when) : text;
}

/** The note as kept: trimmed (line breaks are refused, never folded away). */
export const cleanNote = (note) => String(note == null ? '' : note).trim();

/** Why this note can't be used, or null. */
export function noteProblem(note) {
  const text = cleanNote(note);
  if (!text) return 'add the note for the description (it says why the invoice is booked over)';
  if (/[\r\n]/.test(text)) return 'the note must be one line';
  if (text.length > BOOK_OVER_NOTE_MAX) return `the note is ${text.length} characters — at most ${BOOK_OVER_NOTE_MAX}`;
  return null;
}

/** A line's words with the note after them. Only the invoice's — the order line's own words never change. */
export function describeWithNote(description, note) {
  const words = String(description == null ? '' : description).trim();
  const extra = cleanNote(note);
  if (!extra) return words;
  return words ? `${words}${NOTE_JOIN}${extra}` : extra;
}

/**
 * A quantity split into `count` parts: equal shares, whole packs for a line
 * counted in packs (when there are at least as many as parts), else 2 places;
 * the last part takes the remainder, so they add up exactly.
 */
export function splitQuantity(qty, count, { whole = false } = {}) {
  const total = round4(qty);
  const n = Math.max(1, Math.floor(num(count)));
  if (n === 1) return [total];
  const places = whole && Number.isInteger(total) && Math.abs(total) >= n ? 0 : 2;
  const f = 10 ** places;
  const share = Math.floor((total / n) * f + 1e-9) / f;
  const out = Array.from({ length: n - 1 }, () => round4(share));
  out.push(round4(total - share * (n - 1)));
  return out;
}

/** A part's quantity of an order line (0 when the part does not carry it). */
export function partQty(part, orderLineId) {
  const lines = part && part.lines ? part.lines : null;
  if (!lines) return null;
  const v = lines[String(orderLineId)] ?? lines[Number(orderLineId)];
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? round4(n) : NaN;
}

/**
 * The parts a booking starts with.
 * @param {object} o
 * @param {string} o.deliveryDate   the order's (or the latest of a bulk run's)
 * @param {number} o.count          1–12
 * @param {Array<{ order_line_id, quantity, counted? }>} [o.lines]  for a split
 * @param {string} [o.firstAllowedDate]  Xero's lock + 1
 * @param {boolean} [o.many]        several orders (the note keeps the token)
 * @returns {Array<{ invoice_date, due_date, note, lines: object|null }>}
 */
export function defaultParts({ deliveryDate, count = 1, lines = [], firstAllowedDate = null, many = false, what = 'Delivered' } = {}) {
  const n = Math.min(BOOK_OVER_MAX_PARTS, Math.max(1, Math.floor(num(count)) || 1));
  const shares = new Map();
  if (n > 1) {
    for (const l of lines || []) shares.set(String(l.order_line_id), splitQuantity(l.quantity, n, { whole: !!l.counted }));
  }
  const parts = [];
  let previous = null;
  for (let i = 0; i < n; i += 1) {
    let invoiceDate = laterDay(firstOfMonthAfter(deliveryDate, i + 1), firstAllowedDate && isDay(firstAllowedDate) ? firstAllowedDate : null);
    // Never two parts on one day by default: past the lock, a month on each.
    if (previous && invoiceDate && invoiceDate <= previous) invoiceDate = firstOfMonthAfter(previous, 1);
    previous = invoiceDate;
    const part = {
      invoice_date: invoiceDate || '',
      due_date: invoiceDate ? dueDateFor(invoiceDate) : '',
      note: defaultNote({ deliveryDate, invoiceDate, part: i + 1, of: n, many, what }),
      lines: null
    };
    if (n > 1) {
      part.lines = {};
      for (const [id, list] of shares) part.lines[id] = list[i];
    }
    parts.push(part);
  }
  return parts;
}

/**
 * One line's money on a part, as the invoice is raised
 * (orderInvoiceService.invoiceLinesFor): its total qty × price to the penny,
 * VAT on that total at the line's rate, to the penny.
 */
export function lineMoney(qty, line) {
  const raw = round4(qty) * num(line.unit_price);
  const net = round2(raw);
  const vat = round2(net * num(line.tax_rate));
  return { raw, net, vat };
}

/**
 * A part's net / VAT / total (the lines it carries; the whole order when
 * `part.lines` is null) — as the invoice save totals it
 * (invoiceService._calculateTotals: the net is Σ qty × price to the penny,
 * the VAT Σ each line's).
 */
export function partTotals(part, lines) {
  let raw = 0; let vat = 0;
  for (const l of lines || []) {
    const q = part && part.lines ? partQty(part, l.order_line_id) : num(l.quantity);
    if (!q || Number.isNaN(q)) continue;
    const m = lineMoney(q, l);
    raw += m.raw; vat += m.vat;
  }
  const net = round2(raw);
  vat = round2(vat);
  return { net, vat, total: round2(net + vat) };
}

/** Normalise what a request sent (snake_case or camelCase); null when it is not booking over. */
export function normaliseBookOver(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.mode === 'delivery') return null;
  const list = Array.isArray(raw.parts) && raw.parts.length ? raw.parts : [raw];
  return {
    parts: list.map((p) => {
      const src = p && typeof p === 'object' ? p : {};
      let lines = null;
      if (src.lines && typeof src.lines === 'object') {
        lines = {};
        for (const [id, q] of Object.entries(src.lines)) {
          if (q === '' || q === null || q === undefined) { lines[String(id)] = 0; continue; }
          const n = Number(q);
          lines[String(id)] = Number.isFinite(n) ? round4(n) : NaN;
        }
      }
      return {
        invoice_date: dayOf(src.invoice_date ?? src.invoiceDate ?? ''),
        due_date: dayOf(src.due_date ?? src.dueDate ?? ''),
        note: cleanNote(src.note),
        lines
      };
    })
  };
}

/**
 * What stops this booking, in words — the window greys its button with the
 * first; the server refuses with them all.
 * @param {object} o
 * @param {Array} o.parts       as normaliseBookOver gives them (or the window's)
 * @param {Array<{ order_line_id, quantity, description? }>} [o.lines]  the order's
 *        lines, for a split (checked only when given)
 * @param {string} [o.firstAllowedDate]  Xero's lock + 1 (checked only when known)
 * @returns {string[]}
 */
export function bookOverProblems({ parts, lines = null, firstAllowedDate = null } = {}) {
  const out = [];
  const list = Array.isArray(parts) ? parts : [];
  if (list.length < 1 || list.length > BOOK_OVER_MAX_PARTS) {
    out.push(`Book over into 1 to ${BOOK_OVER_MAX_PARTS} invoices`);
    return out;
  }
  const split = list.length > 1;
  list.forEach((p, i) => {
    const label = split ? `Part ${i + 1}: ` : '';
    const inv = dayOf(p.invoice_date);
    const due = dayOf(p.due_date);
    if (!inv) out.push(`${label}choose the invoice date`);
    else if (!isDay(inv)) out.push(`${label}the invoice date isn't a real date`);
    else if (firstAllowedDate && isDay(firstAllowedDate) && inv < firstAllowedDate) {
      out.push(`${label}${ukDay(inv)} is on or before Xero's lock — choose ${ukDay(firstAllowedDate)} or later`);
    }
    if (!due) out.push(`${label}choose the due date`);
    else if (!isDay(due)) out.push(`${label}the due date isn't a real date`);
    else if (isDay(inv) && due < inv) out.push(`${label}the due date ${ukDay(due)} is before the invoice date ${ukDay(inv)}`);
    const np = noteProblem(p.note);
    if (np) out.push(`${label}${np}`);
  });
  if (split) {
    if (list.some(p => !p.lines)) {
      out.push('Each part needs its quantities');
      return out;
    }
    if (Array.isArray(lines)) {
      const known = new Set(lines.map(l => String(l.order_line_id)));
      for (const l of lines) {
        const name = String(l.description || l.item_code || `line ${l.order_line_id}`).trim();
        const qs = list.map(p => partQty(p, l.order_line_id));
        if (qs.some(q => Number.isNaN(q))) { out.push(`${name}: a part's quantity isn't a number`); continue; }
        if (qs.some(q => q < 0)) { out.push(`${name}: a part's quantity can't be below 0`); continue; }
        const sum = round4(qs.reduce((s, q) => s + q, 0));
        const want = round4(l.quantity);
        if (Math.abs(sum - want) > 0.00005) out.push(`${name}: the parts come to ${formatQuantity(sum)}, not ${formatQuantity(want)}`);
      }
      list.forEach((p, i) => {
        const extra = Object.keys(p.lines || {}).filter(id => !known.has(id) && num(p.lines[id]) !== 0);
        if (extra.length) out.push(`Part ${i + 1} names a line that is not on the order`);
        const carries = lines.some(l => partQty(p, l.order_line_id) > 0);
        if (!carries) out.push(`Part ${i + 1} has no lines — give it a quantity, or split into fewer invoices`);
      });
    }
  }
  return out;
}

/** "booked over to 01/11/2026" / "split into 3 invoices — 01/11/2026, 01/12/2026, 01/01/2027". */
export function bookOverWords(parts) {
  const days = (parts || []).map(p => ukDay(p.invoice_date)).filter(Boolean);
  if (days.length <= 1) return `booked over to ${days[0] || 'another date'}`;
  return `split into ${days.length} invoices — ${days.join(', ')}`;
}
